import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { FormFooter } from '@/components/ui/form-footer';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { categoryLabel, formatShortDate } from '../list/expense-labels';
import { monthLabel, monthRange } from '../list/list-query';
import { formatAmount } from '../money';
import { listUnreported, type PickerExpense } from './api';
import { PHONE_BOTTOM_SHEET } from './phone-sheet';
import type { NoteType } from './types';
import { useAllowedCategories } from './use-allowed-categories';

type Load = 'loading' | 'failed' | 'ready';

function usePicker(open: boolean, period: string, ownerId: string) {
  const [items, setItems] = useState<PickerExpense[]>([]);
  const [load, setLoad] = useState<Load>('loading');
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    const range = monthRange(period);
    if (range === null) {
      setLoad('failed');
      return;
    }
    let live = true;
    setLoad('loading');
    setChosen(new Set());
    void listUnreported(range.from, range.to, ownerId).then((outcome) => {
      if (!live) return;
      if (outcome.ok) setItems(outcome.value);
      setLoad(outcome.ok ? 'ready' : 'failed');
    });
    return () => {
      live = false;
    };
  }, [open, period, ownerId]);
  return { items, load, chosen, setChosen } as const;
}

/**
 * «Aggiungi spese» (spec §1.3): the owner's expenses dated in the report's month that belong to
 * no report, to tick; «Nuova spesa» goes to capture with this report already chosen.
 */
export function AddExpensesSheet({
  open,
  reportId,
  number,
  period,
  ownerId,
  noteType,
  busy,
  onClose,
  onAdd,
}: {
  readonly open: boolean;
  readonly reportId: string;
  readonly number: string;
  readonly period: string;
  readonly ownerId: string;
  /** The note's type: only the categories it accepts are offered (wave 2, R-a). */
  readonly noteType?: NoteType | null;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onAdd: (expenseIds: readonly string[]) => void;
}) {
  const { t, i18n } = useTranslation('expenses');
  const { items: all, load, chosen, setChosen } = usePicker(open, period, ownerId);
  const allowed = useAllowedCategories(noteType);
  const items = allowed === null ? all : all.filter((item) => allowed.has(item.category));
  const hidden = all.length - items.length;

  const toggle = (id: string): void =>
    setChosen((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <SheetContent side="bottom" className={`${PHONE_BOTTOM_SHEET} flex flex-col gap-3 p-4`}>
        <SheetTitle>{t('reports.picker.title', { number })}</SheetTitle>
        <SheetDescription>
          {t('reports.picker.help', { period: monthLabel(period, i18n.resolvedLanguage) })}
        </SheetDescription>
        {load === 'loading' ? <p role="status">{t('list.loading')}</p> : null}
        {load === 'failed' ? (
          <p role="alert" className="text-destructive text-sm">
            {t('reports.picker.failed')}
          </p>
        ) : null}
        {load === 'ready' && hidden > 0 && noteType ? (
          <p role="status" className="text-muted-foreground text-sm">
            {t('reports.picker.hidden', { count: hidden, type: t(`reports.noteType.${noteType}`) })}
          </p>
        ) : null}
        {load === 'ready' && items.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('reports.picker.none')}</p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <PickerRow key={item.id} item={item} checked={chosen.has(item.id)} onToggle={toggle} />
          ))}
        </ul>
        <FormFooter
          reason={chosen.size === 0 ? t('reports.picker.pickReason') : null}
          primaryLabel={
            chosen.size === 0
              ? t('reports.picker.addNone')
              : t('reports.picker.add', { count: chosen.size })
          }
          primaryDisabled={chosen.size === 0 || busy}
          onPrimary={() => onAdd([...chosen])}
          cancelLabel={t('reports.new.cancel')}
          onCancel={onClose}
        >
          <Button asChild variant="ghost" className="min-h-11">
            <Link to={`/expenses/new?reportId=${encodeURIComponent(reportId)}`}>
              {t('reports.picker.newExpense')}
            </Link>
          </Button>
        </FormFooter>
      </SheetContent>
    </Sheet>
  );
}

function PickerRow({
  item,
  checked,
  onToggle,
}: {
  readonly item: PickerExpense;
  readonly checked: boolean;
  readonly onToggle: (id: string) => void;
}) {
  const { i18n } = useTranslation('expenses');
  return (
    <li>
      <label className="border-border flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm">
        <Checkbox checked={checked} onChange={() => onToggle(item.id)} />
        <span className="tabular-nums">{formatShortDate(item.date)}</span>
        <b className="truncate">{item.supplierName || item.description || '—'}</b>
        <span className="text-muted-foreground hidden sm:inline">
          {categoryLabel(item.category, i18n.resolvedLanguage)}
        </span>
        <span className="ml-auto tabular-nums">{formatAmount(item.total)}</span>
      </label>
    </li>
  );
}
